<script lang="ts">
  import type { QuickContact } from '@gat-ship/types'

  export type AddContactQuickModalProps = {
    contact?: Partial<QuickContact>
  }
</script>

<script setup lang="ts">
  import { ref } from 'vue'
  import { globalUtils } from '@gat-ship/utils'
  import {
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupPhone,
    isValidPhoneNumber,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderTitle,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'

  const emit = defineEmits<{
    (e: 'added', contact: QuickContact): void
    (e: 'error', params: { error: unknown; contact: QuickContact }): void
  }>()

  const contactId = ref('')
  const modalTitle = ref('Add Contact Person')
  const submitLabel = ref('Add')

  const phoneValidators = {
    onBlur: ({ value }: { value: string }) =>
      !value || value.length === 0
        ? undefined
        : isValidPhoneNumber(value)
          ? undefined
          : 'Invalid phone number'
  }

  const quickContactSchema = z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().optional(),
    mobilePhone: z.string().optional(),
    directLine: z.string().optional(),
    jobTitle: z.string().optional(),
    department: z.string().optional()
  })
  type QuickContactFormValues = z.infer<typeof quickContactSchema>

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      mobilePhone: '',
      directLine: '',
      jobTitle: '',
      department: ''
    } as QuickContactFormValues,
    validators: {
      onChange: quickContactSchema
    },
    onSubmit: async ({ value }) => {
      const contact: QuickContact = {
        id: contactId.value,
        firstName: value.firstName,
        lastName: value.lastName,
        jobTitle: value.jobTitle || null,
        email: value.email || null,
        directLine: value.directLine || null,
        mobilePhone: value.mobilePhone || null,
        department: value.department || null
      }
      isOpen.value = false
      try {
        emit('added', contact)
      } catch (error) {
        emit('error', { error, contact })
      }
    }
  })

  const isOpen = ref(false)

  const orEmpty = (value: string | null | undefined) => value ?? ''

  function seedQuickContactForm(contact: AddContactQuickModalProps['contact']) {
    form.reset({
      firstName: orEmpty(contact?.firstName),
      lastName: orEmpty(contact?.lastName),
      email: orEmpty(contact?.email),
      mobilePhone: orEmpty(contact?.mobilePhone),
      directLine: orEmpty(contact?.directLine),
      jobTitle: orEmpty(contact?.jobTitle),
      department: orEmpty(contact?.department)
    })
  }

  function showAddContactQuickModal(params: AddContactQuickModalProps) {
    const { contact } = params
    const hasId = Boolean(contact?.id)

    contactId.value = contact?.id ?? globalUtils.functions.uuid()
    modalTitle.value = hasId ? 'Edit Contact Person' : 'Add Contact Person'
    submitLabel.value = hasId ? 'Update Contact' : 'Add Contact'

    seedQuickContactForm(contact)
    isOpen.value = true
  }

  defineExpose({
    showModal: showAddContactQuickModal
  })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        {{ modalTitle }}
      </ModalHeaderTitle>
    </ModalHeader>
    <ModalContent class="flex flex-col gap-5">
      <ModalSection>
        <ModalSectionHeader>
          <ModalSectionHeaderTitle
            >Personal Information</ModalSectionHeaderTitle
          >
        </ModalSectionHeader>
        <FieldGroup>
          <FormField :form="form" name="firstName" label="First name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="lastName" label="Last name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
      <ModalSection>
        <ModalSectionHeader>
          <ModalSectionHeaderTitle>Contact Information</ModalSectionHeaderTitle>
        </ModalSectionHeader>
        <FieldGroup>
          <FormField :form="form" name="email" label="Email">
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="mobilePhone"
            label="Mobile"
            :validators="phoneValidators"
          >
            <template #default="{ field }">
              <InputGroupPhone
                :id="field.name"
                :model-value="field.state.value ?? undefined"
                @update:model-value="(v) => field.handleChange(v ?? '')"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="directLine"
            label="Phone"
            :validators="phoneValidators"
          >
            <template #default="{ field }">
              <InputGroupPhone
                :id="field.name"
                :model-value="field.state.value ?? undefined"
                @update:model-value="(v) => field.handleChange(v ?? '')"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
      <ModalSection>
        <ModalSectionHeader>
          <ModalSectionHeaderTitle
            >Business Related Information</ModalSectionHeaderTitle
          >
        </ModalSectionHeader>
        <FieldGroup>
          <FormField :form="form" name="jobTitle" label="Job Title">
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="department" label="Department">
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" :label="submitLabel" :require-dirty="false" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
